import {
	takeEvery,
	takeLatest,
	put,
	call,
	all,
	fork
} from "@redux-saga/core/effects";
import {
	FETCH_COMPONENTS,
	CREATE_COMPONENT,
	UPDATE_COMPONENT,
	DELETE_COMPONENT,
	SET_DELETE_COMPONENT_SUCCESS,
	SET_DELETE_COMPONENT_FAILED,
	SET_DELETE_COMPONENT_FAILED_RESET
} from "redux/constants/flowDesigner";
import {
	setComponents,
	setSavingComponentFlagSuccess,
	setSaveComponentFailedReset,
	setSaveComponentFailed,
	setUpdatedComponent
} from "redux/actions/flowDesigner/cxfComponentsActionCreator";
import { reloadFlow } from "redux/actions/flowDesigner/flowsActionCreator";
import {
	fetchComponents,
	registerComponent,
	updateComponent,
	deleteComponent
} from "api/FlowDesignerApis";
import { updateUserComponentGroup } from "redux/actions/flowDesigner/componentGroupsActionCreator";
import { handleFetchToolboxData } from "./componentGroups";
import { uploadConnectorZipFile } from "api/ComponentsApi";
import { insertConnectorVersion } from "api/ConnectorVersions";
import { toast } from "react-toastify";
import { urlWithTimestamp } from "helpers/common";

/***** WORKERS *****/

export function* handleGetComponents({ payload }) {
	try {
		let components = yield call(fetchComponents, payload.tenant_id);
		if (!components) {
			components = [];
		}

		components.sortAlphabetical("name");
		yield put(setComponents(components));
	} catch (error) {
		console.error(error);
	}
}

export function* handleCreateComponent({ payload }) {
	try {
		const { configUpload, user, input, groupObj, callback } = payload;
		yield put(setSaveComponentFailedReset());
		const componentResult = yield call(registerComponent, user?.stripeId, input);
		if (!componentResult?.isSuccessful || !componentResult?.resultObject?.id) {
			yield put(setSaveComponentFailed(componentResult?.message || "Failed to upload the connector"));
			return;
		}

		const res = componentResult.resultObject;

		if (!res) {
			yield put(setSaveComponentFailed('Failed to register new connector as component'));

			return;
		}

		const version = input.version || "1.0";

		const connectorZipResult = yield call(
			uploadConnectorZipFile,
			configUpload.files[0],
			user?.stripeId,
			`${res.id}`,
			version
		);

		if (!connectorZipResult?.isSuccessful) {
			yield put(setSaveComponentFailed(connectorZipResult?.message));

			return;
		}

		const zipResultObject = connectorZipResult?.resultObject;

		const configFiles = zipResultObject?.uploadResults
			?.map((r) => urlWithTimestamp(r.Location))
			.join(",");

		const updateComponentInput = {
			...input,
			config_files: configFiles,
			logo: zipResultObject.imageLocation
		};

		callback({
			...updateComponentInput,
			id: res.id,
			tenant_id: user?.stripeId,
			updated_date_time: new Date().toISOString()
		});
		yield call(updateComponent, user?.stripeId, res.id, updateComponentInput);

		const insertResult = yield call(
			insertConnectorVersion,
			user.stripeId,
			res.id,
			configFiles,
			zipResultObject.fileName ?? "",
			zipResultObject.fileSize ?? 0,
			version
		);

		if (insertResult.success) {

			if (groupObj) {
				if (!groupObj.components) {
					groupObj.components = [];
				}
				groupObj.components = [
					...groupObj.components,
					{ id: res.id, name: input.name, is_hidden: false }
				];
				groupObj.component_groups = [
					...groupObj.component_groups,
					{ is_hidden: false, component_id: res.id }
				];
				const { components, id, created_date_time, ...restGroupObj } =
					groupObj;

				yield put(
					updateUserComponentGroup(user?.stripeId, groupObj.id, restGroupObj)
				);
			}

			const refreshedComponents = yield call(fetchComponents, user?.stripeId);
			yield put(setComponents(refreshedComponents || []));

			yield put(setSavingComponentFlagSuccess());
		} else {
			const deleteResponse = yield call(deleteComponent, user?.stripeId, res.id); // Despite validation errors, the connector was actually installed and therefore needs to be removed.
			
			let errorMessage;

			if (deleteResponse?.isSuccessful)
				errorMessage = insertResult.message;
			else errorMessage = `${insertResult.message}. Please reload this screen, then manually delete this connector.`;
			
			yield put(setSaveComponentFailed(errorMessage));
		}
	} catch (error) {
		yield put(setSaveComponentFailed(`Unable to create new connector: ${error.message}`));
	}
}

export function* handleUpdateComponent({ payload }) {
	try {
		const { user, id, input } = payload;

		yield put(setSaveComponentFailedReset());

		const res = yield call(updateComponent, user?.stripeId, id, input);

		if (res?.isSuccessful !== false && res) {
			yield put(setUpdatedComponent(res));
			yield put(reloadFlow());
		} else {
			if (res?.message) toast.error(res.message);
			yield put(setSaveComponentFailed());
		}
	} catch (error) {
		yield put(setSaveComponentFailed());
	}
}

export function* handleDeleteComponent({ payload }) {
	try {
		const { tenant_id, component_id } = payload;
		yield put({ type: SET_DELETE_COMPONENT_FAILED_RESET });
		const response = yield call(deleteComponent, tenant_id, component_id);

		if (!response?.isSuccessful) {
			toast.error(response?.message || "Failed to delete connector");
			yield put({ type: SET_DELETE_COMPONENT_FAILED });
			return;
		}

		const refreshedComponents = yield call(fetchComponents, tenant_id);
		yield put(setComponents(refreshedComponents || []));
		yield handleFetchToolboxData({ payload });
		yield put({ type: SET_DELETE_COMPONENT_SUCCESS });
		toast.success("Component successfully unregistered.");
	} catch (error) {
		yield put({ type: SET_DELETE_COMPONENT_FAILED });
	}
}

/***** WATCHERS *****/

export function* watchGetComponents() {
	yield takeEvery(FETCH_COMPONENTS, handleGetComponents);
}
export function* watchCreateComponent() {
	yield takeEvery(CREATE_COMPONENT, handleCreateComponent);
}
export function* watchUpdateComponent() {
	yield takeEvery(UPDATE_COMPONENT, handleUpdateComponent);
}
export function* watchDeleteComponent() {
	yield takeLatest(DELETE_COMPONENT, handleDeleteComponent);
}

export function* rootComponentsSaga() {
	yield all([
		fork(watchGetComponents),
		fork(watchCreateComponent),
		fork(watchUpdateComponent),
		fork(watchDeleteComponent)
	]);
}
